import React from "react";

import {
  faCheck,
  faChevronRight,
  faCity,
  faLeaf,
  faStopwatch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimationOnScroll } from "react-animation-on-scroll";
import { Button, Card, Col, Container, Image, Row } from "react-bootstrap";
import { NavLink } from "react-router-dom";

import BannerComponent from "@/components/Banner.component";
import DocumentComponent from "@/components/Document.component";
import ScheduleComponent from "@/components/Schedule.component";
import SectionHeader from "@/components/SectionHeader.component";
import { getUrlLangPrefix } from "@/utils/Local";

import i18n from "../i18n";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

interface IKitCard {
  title: string;
  description: string;
  img: string;
  cta: string;
}

interface IHighlight {
  title: string;
  description: string;
  icon: IconDefinition;
}

const kits: IKitCard[] = [
  {
    title: "Triplex",
    description:
      "Three units in a compact multi-storey format, suited to projects looking to add density with a repeatable building approach.",
    img: "/images/multi-residential-kits/PakVille_Triplex.png",
    cta: "Request Triplex Details",
  },
  {
    title: "Sixplex",
    description:
      "A six-unit building for developers planning more housing on a site or across a portfolio. Contact our team to discuss project requirements.",
    img: "/images/multi-residential-kits/PakVille_Sixplex.png",
    cta: "Ask About the Sixplex",
  },
];

const highlights: IHighlight[] = [
  {
    title: "Faster Assembly",
    description:
      "Advanced composite panels shorten the on-site assembly of the building envelope.",
    icon: faStopwatch,
  },
  {
    title: "Designed to Scale",
    description:
      "Adapt a coordinated building approach across multiple lots or developments.",
    icon: faCity,
  },
  {
    title: "Energy Efficient",
    description:
      "Continuous insulation and reduced thermal bridging support a high-performance envelope.",
    icon: faLeaf,
  },
];

const components: string[] = [
  "PakVert™ wall and roof panels",
  "PakVille™ plumbing walls",
  "Floor systems",
  "Engineering coordination",
  "Connection components",
  "Installation support",
];

const MultiResidentialKits = (): React.JSX.Element => {
  const { t } = i18n;
  const langPrefix = getUrlLangPrefix();
  const contactLink = `${langPrefix}/${t("_route.contact-us")}`;

  return (
    <React.Fragment>
      <DocumentComponent title={t("Multi-Residential Kits")} />
      <BannerComponent description="" title={t("Multi-Residential Kits")} />

      <Container fluid as="section">
        <Container>
          <Row>
            {kits.map((kit, index) => (
              <Col key={kit.title} className="mb-5 mb-lg-4" lg={6}>
                <Card className="p-0 h-100">
                  <NavLink to={contactLink}>
                    <Card.Img
                      alt={t(kit.title)}
                      className="modelCardImg"
                      loading={index === 0 ? "eager" : "lazy"}
                      src={kit.img}
                      variant="top"
                    />
                  </NavLink>

                  <Card.Body>
                    <Card.Title className="m-0 mb-2">
                      <h2 className="modelHeader text-start m-0">
                        {t(kit.title)}
                      </h2>
                    </Card.Title>
                    <Card.Text>{t(kit.description)}</Card.Text>
                  </Card.Body>

                  <Card.Footer
                    className="mb-2"
                    style={{ background: "unset", border: "unset" }}
                  >
                    <NavLink to={contactLink}>
                      <Button className="btn-primary w-100">
                        {t(kit.cta)}
                        <FontAwesomeIcon
                          className="ms-2"
                          icon={faChevronRight}
                        />
                      </Button>
                    </NavLink>
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </Container>

      <Container
        fluid
        as="section"
        className="pt-4 pb-4"
        style={{ backgroundColor: "var(--bg-light-color)" }}
      >
        <Container>
          <SectionHeader
            eyebrow={t("Built for Multi-Residential Development")}
            title={t("A Simpler Way to Deliver Multi-Unit Housing")}
            description={t(
              "PakVille™ combines coordinated building designs with advanced structural composite panel technology to simplify construction, accelerate on-site assembly and help developers scale from one building to an entire development.",
            )}
          />

          <Row className="g-3 g-md-4 mt-4">
            {highlights.map((item) => (
              <Col key={item.title} md={4} xs={12}>
                <div className="iconCard">
                  <span aria-hidden="true" className="iconCardBadge">
                    <FontAwesomeIcon icon={item.icon} />
                  </span>
                  <h3>{t(item.title)}</h3>
                  <p>{t(item.description)}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </Container>

      <Container fluid as="section" className="pt-4 pb-4">
        <Container>
          <Row className="pt-2 pb-2 mt-2 mt-lg-0">
            <Col className="mt-4 mt-lg-0 order-2 order-lg-1" lg={6} xs={12}>
              <AnimationOnScroll animateOnce animateIn="animate__fadeInLeftBig">
                <div className="ratio ratio-16x9 text-center mt-4 mb-4 ">
                  <iframe
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    className="embed-responsive-item w-100 rounded"
                    height="315"
                    src="https://www.youtube.com/embed/vG2Cv1pLTnE?si=Rey3is6a5AZaUlZc"
                    title={t("PakVille™ Home Kits")}
                    width="560"
                  />
                </div>
              </AnimationOnScroll>
            </Col>

            <Col className="order-1 order-lg-2 ps-0 ps-lg-5" lg={6} xs={12}>
              <div
                className="flexContainer installationContent"
                style={{ gap: "8px" }}
              >
                <SectionHeader
                  eyebrow={t("How It Comes Together")}
                  title={t("From Foundation to Building Envelope")}
                  description={t(
                    "See the sequence of a PakVille multi-residential build, from advanced composite panels and prefabricated components to on-site assembly.",
                  )}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </Container>

      <Container
        fluid
        as="section"
        className="pt-4 pb-4"
        style={{ backgroundColor: "var(--bg-light-color)" }}
      >
        <Container className="">
          <SectionHeader
            eyebrow={t("Project Scope")}
            title={t("A Coordinated Building-Kit Approach")}
            description={t(
              "PakVille™ can coordinate the core building-envelope components around your project. Confirm the exact supply and service scope in the project proposal.",
            )}
          />

          <Row className="g-3 g-md-4">
            {components.map((item) => (
              <Col key={item} md={4} xs={6}>
                <div className="iconCard iconCard--compact flex-row align-items-center gap-4 justify-content-start">
                  <span aria-hidden="true" className="iconCardBadge m-0">
                    <FontAwesomeIcon icon={faCheck} />
                  </span>
                  <h3>{t(item)}</h3>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </Container>

      <Container fluid as="section" className="pt-4 pb-4">
        <Container className="flexContainer mt-5 mb-5">
          <Row className="gx-5">
            <Col className="order-2 order-lg-1 mt-5 mt-lg-0" lg={5} xs={12}>
              <div className="flexContainer" style={{ flexDirection: "row" }}>
                <Row>
                  <Col xs={6}>
                    <AnimationOnScroll
                      animateOnce
                      animateIn="animate__fadeInLeft"
                    >
                      <Image
                        rounded
                        alt={t("Traditional Wall Assembly (7 Components)")}
                        className="mw-100"
                        src="/images/multi-residential-kits/Traditional_Wall_Assembly.png"
                      />
                      <figcaption>
                        {t("Traditional Wall Assembly (7 Components)")}
                      </figcaption>
                    </AnimationOnScroll>
                  </Col>

                  <Col xs={6}>
                    <AnimationOnScroll
                      animateOnce
                      animateIn="animate__fadeInRight"
                    >
                      <Image
                        rounded
                        className="mw-100"
                        src="/images/multi-residential-kits/PakVert_all_in_one.png"
                        alt={t(
                          "PakVert™ Panel (1 panel replaces 7 components)",
                        )}
                      />
                      <figcaption>
                        {t("PakVert™ Panel (1 panel replaces 7 components)")}
                      </figcaption>
                    </AnimationOnScroll>
                  </Col>
                </Row>
              </div>
            </Col>

            <Col className="order-1 order-lg-2" lg={7} xs={12}>
              <div
                className="flexContainer installationContent"
                style={{ gap: "8px" }}
              >
                <SectionHeader
                  eyebrow={t("The Technology Behind The Kit")}
                  title={t("Powered by PakVert™")}
                  description={t(
                    "PakVert™ is an advanced structural composite panel technology that combines structure, insulation, vapor barrier and integrated finishes in one lightweight system. It helps simplify the building envelope while supporting energy-efficient construction.",
                  )}
                />

                <NavLink to={contactLink}>
                  <Button className="btn-primary">
                    {t("Ask About PakVert™")}
                  </Button>
                </NavLink>
              </div>
            </Col>
          </Row>
        </Container>
      </Container>

      <ScheduleComponent />
    </React.Fragment>
  );
};

export default MultiResidentialKits;
